import React, { useState, useMemo, useRef, useEffect } from 'react';
import Pagination from '@components/Layout/Pagination';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { BiSort } from 'react-icons/bi';
import HelpAndSupportTable from '@components/Layout/HelpAndSupportTable';
import { CREATE_NOTIFICATION_PATH } from '@constants/routes';
import { useNavigate } from 'react-router-dom';
import SearchBarHelp from '@components/Layout/SearchBarHelp';
import { IoEllipse } from 'react-icons/io5';
import { MdCall } from 'react-icons/md';
import { IoMail } from 'react-icons/io5';
import { ScanCommand } from '@aws-sdk/client-dynamodb';
import { dbClient } from '@utils/awsClients';
import { AttributeValue } from '@aws-sdk/client-dynamodb';

const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
};

type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

type UserNotificationData = {
    notificationDate: string;
    message: string;
    status: string;
};

// Add type definition for the DynamoDB response
interface HelpAndSupportItem {
    uuid: { S: string };
    answer: { S: string };
    question: { S: string };
    sort_order: { N: string };
}

// Add interface for the transformed data
interface TransformedHelpAndSupport {
    uuid: string;
    answer: string;
    question: string;
    sort_order: number;
}

const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn !bg-primary text-white"
    >
        {children}
        <BiSort className="ml-2" /> {/* Add your custom icon here */}
    </button>
));
const HelpAndSupport: React.FC = () => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortField, setSortField] = useState<string>('');
    const navigate = useNavigate();
    const [isCardVisible, setIsCardVisible] = useState(true);
    const cardRef = useRef<HTMLDivElement>(null);
    const [helpAndSupport, setHelpAndSupport] = useState<
        TransformedHelpAndSupport[]
    >([]);

    useEffect(() => {
        const fetchHelpAndSupport = async () => {
            try {
                const command = new ScanCommand({
                    TableName: 'helpandsupport',
                });

                const response = await dbClient.send(command);
                if (response.Items) {
                    // Transform the DynamoDB format to a simpler format
                    const transformedData: TransformedHelpAndSupport[] =
                        response.Items.map(
                            (item: Record<string, AttributeValue>) => ({
                                uuid: item.uuid?.S ?? '',
                                answer: item.answer?.S ?? '',
                                question: item.question?.S ?? '',
                                sort_order: parseInt(item.sort_order?.N ?? '0'),
                            })
                        );
                    setHelpAndSupport(transformedData);
                }
            } catch (error) {
                console.error('Error fetching help and support data:', error);
                // Handle error appropriately
            }
        };

        fetchHelpAndSupport();
    }, []);

    const handelCreateNotification = () => {
        navigate(CREATE_NOTIFICATION_PATH);
    };

    const handleSortChange = (event: string) => {
        setSortField(event);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };
    const handleToggleCardVisibility = () => {
        setIsCardVisible(!isCardVisible);
        if (!isCardVisible) {
            setTimeout(() => {
                cardRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 0);
        }
    };
    const filteredUsers = useMemo(() => {
        return helpAndSupport;
    }, [helpAndSupport]);

    const sortedData = useMemo(() => {
        const filteredUsersArray = filteredUsers || [];
        let sorted = [...filteredUsersArray];

        // Sort by sort_order by default
        sorted.sort((a, b) => a.sort_order - b.sort_order);

        return sorted;
    }, [filteredUsers]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, rowsPerPage]);


    const renderEllipsis = () => {
        return (
            <span
                key="ellipsis"
                className="bg-white pagination-padding py-2 border rounded"
            >
                ...
            </span>
        );
    };
    return (
        <>
            <div className="h-screen">
                <div className="d-flex justify-content-center align-items-center mb-4 pt-5">
                    <h1 className="text-lg font-bold">
                        {t('help_and_support.title')}
                    </h1>
                </div>

                <HelpAndSupportTable
                    data={paginatedUsers}
                    setCardVisible={setIsCardVisible}
                />

                <div
                    ref={cardRef}
                    className={`position-relative bottom-8 transform -translate-x-1/2 transition-transform duration-300 left-1/2 w-80 h-48 bg-primary text-white rounded-2xl p-5 flex flex-col items-center z-50 ${isCardVisible ? 'help-card' : 'help-card-expanded'}`}
                >
                    <span className="text-lg font-bold mb-2">
                    {t('help_and_support.help')}
                    </span>
                    <span className="text-sm mb-4">
                        {t('help_and_support.support')}
                    </span>
                    <div className="flex justify-between w-9/12 pr-7">
                        <a href="tel:+918072011799" aria-label="call" className="flex items-center relative">
                            <IoEllipse size={58} className="absolute" />
                            <MdCall size={25} className="relative text-primary left-4" />
                        </a>
                        <a href="mailto:technical@emsense.tech" aria-label="chat" className="flex items-center relative">
                            <IoEllipse size={58} className="absolute" />
                            <IoMail size={25} className="relative text-primary left-4" />
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HelpAndSupport;
